import { ComponentType } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DataHelper } from '@cdw-ae/common-frontend-data';
import {
  CustomValidator,
  FieldMatcher,
  FilterBarField,
  isFilterBarField,
  isSuggestedMatcher,
  SelectedFilterField,
  SuggestedMatcher,
} from '@cdw-ae/filter-bar-interfaces';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
// Temporarily commented out for core library
// import { FilterBarDialogComponent } from './filter-bar-dialog/filter-bar-dialog.component';

@Component({
  selector: 'cdw-ae-filter-bar-core',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './filter-bar.component.html',
  styleUrls: ['./filter-bar.component.scss'],
  standalone: false,
})
export class FilterBarComponent implements OnInit, OnDestroy {
  public filterState = new FormGroup({
    filterBarInput: new FormControl<string>(''),
    selectedFilters: new FormControl<SelectedFilterField[]>([], {
      nonNullable: true,
    }),
  });

  public filteredFieldList: Observable<FilterBarField[]>;

  public suggestedMatchersList: SuggestedMatcher[] = [];

  private filterBarInput: string | null = null;

  /**
   * The last filter set this bar emitted to the parent. Used to recognise (and
   * skip) the echo of our own change when it round-trips back through
   * `initialSelectedFilterFields$` (state → URL → back into this input), so an
   * external-sync never clobbers an in-progress user edit or loops.
   */
  private lastEmittedFilters: SelectedFilterField[] = [];

  @Input()
  public filterBarField: FilterBarField[] = [];

  @Input()
  public initialSelectedFilterFields$:
    | Observable<SelectedFilterField[]>
    | undefined;

  @Input()
  public fieldMask$: Observable<string[]> | undefined;

  @Input()
  public limitOneFilter$: Observable<boolean> | undefined;

  @Input()
  public autoLimitField = true;

  @Input()
  public dialogComponent?: ComponentType<unknown>;

  @Output() filtersChange = new EventEmitter<SelectedFilterField[]>();

  @Output() filterBarInputChanged = new EventEmitter<string | null>();

  @ViewChild('filterInput') private filterInput!: ElementRef<HTMLInputElement>;

  @ViewChild('autoCompletePanel') public autocompletePanel!:
    | MatAutocomplete
    | undefined;

  @ViewChild(MatAutocompleteTrigger) private autocompleteTrigger!:
    | MatAutocompleteTrigger
    | undefined;

  public limitOneFilter = false;

  public fieldMask: string[] = [];

  private onDestroy$: Subject<void>;

  constructor(
    public filterValueDialog: MatDialog,
    private changeDetector: ChangeDetectorRef
  ) {
    this.onDestroy$ = new Subject<void>();

    this.filterState.controls.filterBarInput.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value: string | null) => {
        this.filterBarInputChanged.emit(value);
        this.filterBarInput = value;
      });

    this.filterState.controls.selectedFilters.valueChanges
      .pipe(
        map((filters: SelectedFilterField[]): SelectedFilterField[] => {
          if (Array.isArray(this.fieldMask) && this.fieldMask.length === 0) {
            return filters;
          }

          const maskedSelectedFilters: SelectedFilterField[] = filters.filter(
            (selectedFilterField: SelectedFilterField) =>
              this.fieldMask.indexOf(selectedFilterField.field.name) !== -1
          );
          if (!this.limitOneFilter) {
            return maskedSelectedFilters;
          }
          return maskedSelectedFilters.length ? [maskedSelectedFilters[0]] : [];
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe((filters: SelectedFilterField[]) => {
        // Record what we emit so the round-tripped echo of our own change can be
        // skipped by the external-sync subscription in ngOnInit.
        this.lastEmittedFilters = filters;
        this.filtersChange.emit(filters);
      });

    this.filteredFieldList =
      this.filterState.controls.filterBarInput.valueChanges.pipe(
        startWith(null),
        takeUntil(this.onDestroy$),
        map(() => {
          this.defaultFilterSetup();
          return this.filterBarField.slice();
        }),
        map((filterBarFields: FilterBarField[]) =>
          filterBarFields.sort(FilterBarComponent.compareBySortPriority)
        )
      );
  }

  public ngOnInit(): void {
    this.filterBarField = this.filterBarField.map((filter) => ({ ...filter }));

    if (this.fieldMask$) {
      this.fieldMask$.subscribe((fieldMask: string[]) => {
        this.fieldMask = fieldMask;
      });
    }

    if (this.limitOneFilter$) {
      this.limitOneFilter$.subscribe((limitOneFilter: boolean) => {
        this.limitOneFilter = limitOneFilter;
      });
    }

    if (this.initialSelectedFilterFields$) {
      // Stay subscribed (not take(1)) so the chips track external filter-state
      // changes on EVERY navigation — including browser back/forward and
      // share-link opens — not just the first load. (EDUS-2600: previously the
      // grid de-filtered on back-nav while the pills stayed, because this only
      // seeded once.)
      this.initialSelectedFilterFields$
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((externalFilters: SelectedFilterField[]) => {
          const incoming = externalFilters ?? [];

          // Skip the echo of our own change: when the user edits filters we emit
          // → state updates → that same value comes back through this stream.
          // Re-applying it would clobber an in-progress edit and loop. Only act
          // on a genuinely external value (URL/back-nav) that differs from what
          // we last emitted.
          if (this.filtersContentEqual(incoming, this.lastEmittedFilters)) {
            return;
          }

          // Apply the external change WITHOUT re-emitting: it originated from the
          // URL/state, so echoing it back would re-publish to the URL and can
          // interfere with browser back/forward history. (The grid refetches on
          // its own via cdw-ssrm-grid's ngOnChanges, so no emit is needed here.)
          this.filterState.controls.selectedFilters.setValue(incoming, {
            emitEvent: false,
          });
          this.lastEmittedFilters = incoming;

          // Recompute singleUse hidden states for the current set: reset all,
          // then hide the ones now selected — so removals on back-nav re-expose
          // their fields, not just additions hiding them.
          this.filterBarField.forEach((field: FilterBarField) => {
            if (field.singleUse) {
              field.hidden = false;
            }
          });
          incoming.forEach((filter: SelectedFilterField) => {
            this.toggleHiddenFilter(filter.field, true);
          });

          // Force filteredFieldList to recalculate against the updated hidden
          // states (it derives from filterBarInput.valueChanges).
          const currentValue = this.filterState.controls.filterBarInput.value;
          this.filterState.controls.filterBarInput.setValue(currentValue, {
            emitEvent: true,
          });
          this.changeDetector.detectChanges();
        });
    }
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Content equality for selected-filter arrays, keyed on the logically-
   * meaningful parts only ({@link SelectedFilterField.field} name,
   * {@link SelectedFilterField.selectedMatcher} name, and `matchValue`). It
   * deliberately ignores the per-chip random `uid` and non-serializable matcher
   * option functions, so the echo of our own emission is recognised even though
   * it round-trips as fresh object instances.
   */
  private filtersContentEqual(
    a: SelectedFilterField[],
    b: SelectedFilterField[]
  ): boolean {
    const key = (fs: SelectedFilterField[]): string =>
      JSON.stringify(
        (fs ?? []).map((f: SelectedFilterField) => [
          f.field?.name,
          f.selectedMatcher?.name,
          f.matchValue,
        ])
      );
    return key(a) === key(b);
  }

  private defaultFilterSetup(): void {
    this.filterBarField = this.filterBarField.map((filter: FilterBarField) => ({
      ...filter,
      singleUse: filter?.singleUse !== false,
    }));
  }

  public clearFilters(): void {
    this.filterState.controls.selectedFilters.setValue([]);

    // Unhide ALL singleUse filters by resetting hidden property
    this.filterBarField.forEach((field) => {
      if (field.singleUse) {
        field.hidden = false;
      }
    });

    if (this.autocompleteTrigger?.panelOpen) {
      this.autocompleteTrigger.closePanel();
    }

    // filteredFieldList derives from filterBarInput.valueChanges — setting '' to ''
    // won't emit. Set a temp value silently first so the subsequent '' counts as a change
    // and re-runs the pipeline that reads the (now-unhidden) filterBarField array.
    this.filterState.controls.filterBarInput.setValue(' ', {
      emitEvent: false,
    });
    this.filterState.controls.filterBarInput.setValue('');

    // OnPush won't detect the mutated hidden properties — force change detection
    this.changeDetector.detectChanges();
  }

  public clearInput(): void {
    if (this.autocompletePanel?.isOpen) {
      return;
    }

    this.filterState.controls.filterBarInput.setValue(null);
    this.filterInput.nativeElement.value = '';
  }

  protected removeFilter(filterToRemove: SelectedFilterField): void {
    const index = this.filterState.controls.selectedFilters.value?.findIndex(
      (curFilter: SelectedFilterField) => curFilter.uid === filterToRemove.uid
    );
    if (index >= 0) {
      const newSelectedFilters = [
        ...this.filterState.controls.selectedFilters.value,
      ];
      newSelectedFilters.splice(index, 1);
      this.filterState.controls.selectedFilters.setValue(newSelectedFilters);
    }

    this.toggleHiddenFilter(filterToRemove.field, false);

    // Force change detection since allFiltersSelected depends on mutated filterBarField
    this.changeDetector.detectChanges();
  }

  protected upsertFilterValue(filterToUpsert: SelectedFilterField) {
    const index = this.filterState.controls.selectedFilters.value?.findIndex(
      (curFilter: SelectedFilterField) => curFilter.uid === filterToUpsert.uid
    );
    const newSelectedFilters = [
      ...this.filterState.controls.selectedFilters.value,
    ];
    if (index >= 0) {
      newSelectedFilters[index] = filterToUpsert;
    } else {
      newSelectedFilters.push(filterToUpsert);
    }

    this.toggleHiddenFilter(filterToUpsert.field, true);
    this.filterState.controls.selectedFilters.setValue(newSelectedFilters);

    // Force change detection since allFiltersSelected depends on mutated filterBarField
    this.changeDetector.detectChanges();
  }

  protected startNewFilter(event: MatAutocompleteSelectedEvent): void {
    let selectedFilterField!: SelectedFilterField;

    if (isFilterBarField(event.option.value)) {
      selectedFilterField = {
        uid: DataHelper.makeUUID(),
        field: event.option.value,
        selectedMatcher: event.option.value.fieldMatchers[0],
        matchValue: this.filterInput?.nativeElement?.value || null,
      };
    }

    if (isSuggestedMatcher(event.option.value)) {
      selectedFilterField = {
        uid: DataHelper.makeUUID(),
        field: event.option.value.field,
        selectedMatcher: event.option.value.suggestedMatcher,
        matchValue: this.filterInput.nativeElement.value,
      };
    }

    if (!selectedFilterField) {
      return;
    }

    if (!selectedFilterField.selectedMatcher?.hasEditor) {
      this.saveNonEditFilter(selectedFilterField);
    }

    if (selectedFilterField.matchValue) {
      this.upsertFilterWithSuggestedMatcher(selectedFilterField);
      return;
    }

    this.editFilter(selectedFilterField);
  }

  protected isEditableChip = (filter: SelectedFilterField): boolean =>
    filter.selectedMatcher?.hasEditor === true;

  protected saveNonEditFilter(mutableSelectedFilter: SelectedFilterField) {
    mutableSelectedFilter.matchValue = mutableSelectedFilter.selectedMatcher
      ?.options?.setValue
      ? mutableSelectedFilter.selectedMatcher.options.setValue()
      : 'true';
    this.upsertFilterValue(mutableSelectedFilter);
    this.filterState.controls.filterBarInput.setValue(null);
    this.filterInput.nativeElement.value = '';
    this.filterInput.nativeElement.blur();
  }

  protected upsertFilterWithSuggestedMatcher(
    selectedFilterField: SelectedFilterField
  ) {
    this.upsertFilterValue(selectedFilterField);
    this.filterState.controls.filterBarInput.setValue(null);
    this.filterInput.nativeElement.value = '';
    this.filterInput.nativeElement.blur();
  }

  protected editFilter(
    selectedFilter: SelectedFilterField,
    chipClick?: MouseEvent
  ): void {
    // Only open dialog if dialog component is provided
    if (!this.dialogComponent) {
      return;
    }
    let mutableSelectedFilter = selectedFilter;

    const baseRect = chipClick
      ? (
          chipClick.target as unknown as HTMLInputElement
        ).parentElement?.getBoundingClientRect()
      : this.filterInput.nativeElement.getBoundingClientRect();

    // Re-hydrate non-serializable options (e.g. validateFn) from the live
    // filterBarField config. Function references are stripped when filters are
    // restored from URL/store on page refresh, so the live config is the source
    // of truth for anything that can't survive serialization.
    const liveField = this.filterBarField.find(
      (f) => f.name === mutableSelectedFilter.field.name
    );
    const liveMatcher = liveField?.fieldMatchers?.find(
      (m) => m.name === mutableSelectedFilter.selectedMatcher?.name
    );
    if (liveMatcher?.options && mutableSelectedFilter.selectedMatcher) {
      mutableSelectedFilter = {
        ...mutableSelectedFilter,
        selectedMatcher: {
          ...mutableSelectedFilter.selectedMatcher,
          options: {
            ...mutableSelectedFilter.selectedMatcher.options,
            ...liveMatcher.options,
          },
        },
      };
    }

    const dialogWidth = mutableSelectedFilter.selectedMatcher?.options?.dialogWidth ?? '300px';
    const dialogOptions: MatDialogConfig = {
      width: dialogWidth,
      maxHeight: '550px',
      data: mutableSelectedFilter,
      autoFocus: false,
    };

    if (baseRect) {
      dialogOptions.position = {
        top: `${(baseRect?.top ?? 0) - 15}px`,
        left: `${baseRect?.left ?? 0}px`,
      };
    }

    const dialogRef = this.filterValueDialog.open(
      this.dialogComponent,
      dialogOptions
    );

    dialogRef.afterClosed().subscribe((matchValue: string) => {
      if (matchValue) {
        if (this.limitOneFilter) {
          this.clearFilters();
        }
        this.upsertFilterValue({ ...mutableSelectedFilter, matchValue });
      }

      this.filterState.controls.filterBarInput.setValue(null);
      this.filterInput.nativeElement.value = '';
      this.filterInput.nativeElement.blur();
    });
  }

  protected getFormattedValue = (filter: SelectedFilterField): string =>
    filter.selectedMatcher.options?.chipFormatter
      ? filter.selectedMatcher.options?.chipFormatter(
          (filter.matchValue as string) ?? ''
        )
      : ((filter.matchValue as string) ?? '');

  protected getToolTip = (filter: SelectedFilterField): string => {
    if (!this.isEditableChip(filter)) {
      return '';
    }
    return filter.selectedMatcher.options?.toolTipFormatter
      ? filter.selectedMatcher.options.toolTipFormatter(
          filter.matchValue as string
        )
      : (filter.matchValue as string);
  };

  public updateSuggestedMatchers(): void {
    this.suggestedMatchersList = this.filterBarField
      .slice()
      .map(
        (filterBarField: FilterBarField): FilterBarField => ({
          ...filterBarField,
          masked:
            Array.isArray(this.fieldMask) &&
            this.fieldMask.length > 0 &&
            this.fieldMask.indexOf(filterBarField.name) === -1,
        })
      )
      .reduce(
        (
          suggestedMatchers: SuggestedMatcher[],
          field: FilterBarField
        ): SuggestedMatcher[] => {
          const foundMatchers = field.fieldMatchers
            .filter((matcher: FieldMatcher) => matcher.suggestMatcher)
            .filter((matcher: FieldMatcher) =>
              matcher.validators ? this.validateInput(matcher.validators) : true
            )
            .map(
              (defaultMatcher: FieldMatcher): SuggestedMatcher => ({
                field,
                suggestedMatcher: defaultMatcher,
              })
            );
          return [...suggestedMatchers, ...foundMatchers];
        },
        []
      )
      .sort((a: SuggestedMatcher, b: SuggestedMatcher) =>
        FilterBarComponent.compareBySortPriority(a.field, b.field)
      );
  }

  /**
   * Orders fields by ascending `sortPriority` (unset priorities sort last),
   * falling back to alphabetical by `displayName`. `Infinity - Infinity` is
   * `NaN` when neither field has a priority, so NaN must be treated as "no
   * difference" rather than a truthy comparator result.
   */
  private static compareBySortPriority(
    a: FilterBarField,
    b: FilterBarField
  ): number {
    const priorityDelta =
      (a.sortPriority ?? Number.POSITIVE_INFINITY) -
      (b.sortPriority ?? Number.POSITIVE_INFINITY);
    if (!Number.isNaN(priorityDelta) && priorityDelta !== 0) {
      return priorityDelta;
    }
    if (a.displayName === b.displayName) {
      return 0;
    }
    return a.displayName > b.displayName ? 1 : -1;
  }

  /**
   * Check if all available filters are already selected
   * Considers singleUse filters that are hidden and fieldMask restrictions
   */
  public get allFiltersSelected(): boolean {
    // Get available filters (not hidden and within fieldMask if applicable)
    const availableFilters = this.filterBarField.filter((field) => {
      // Filter out hidden fields (singleUse filters already selected)
      if (field.hidden === true) {
        return false;
      }

      // Filter based on fieldMask if it's set
      if (Array.isArray(this.fieldMask) && this.fieldMask.length > 0) {
        return this.fieldMask.includes(field.name);
      }

      return true;
    });

    // If there are available filters, not all are selected
    return availableFilters.length === 0;
  }

  public addFilterButtonClick(trigger: MatAutocompleteTrigger) {
    this.filterState.controls.filterBarInput.setValue(null);
    this.filterInput.nativeElement.value = '';
    trigger.openPanel();
    this.changeDetector.detectChanges();
    this.filterInput.nativeElement.focus();
  }

  private validateInput(validators: CustomValidator[]): boolean {
    const validatorFns: ValidatorFn[] = validators.map(
      (customValidator: CustomValidator): ValidatorFn =>
        customValidator.validator
    );
    return new FormControl(this.filterBarInput, validatorFns).valid;
  }

  private toggleHiddenFilter(fieldToToggle: FilterBarField, newValue: boolean) {
    if (fieldToToggle.singleUse) {
      for (const field of this.filterBarField) {
        if (
          field.name === fieldToToggle.name &&
          field.displayName === fieldToToggle.displayName
        ) {
          field.hidden = newValue;
          break;
        }
      }
    }
  }
}
